import Fuse from 'fuse.js';
import { VCPM } from '../models/project.model';

export interface ReadyNodeFilterCriteria {
  vendor?: string;
  vendorId?: string;      // takes precedence over vendor when present - ids are the correct semantic key
  profile?: string;
  generation?: string;
  generationId?: string;  // takes precedence over generation when present
  serverModel?: string;
  minCapacity?: number;
  maxCapacity?: number;
}

export interface ReadyNodeGeneration {
  id: string;
  name: string;
}

export interface ReadyNodeServer {
  name: string;
  generations: ReadyNodeGeneration[]; // Array of generation objects with id and name
}

export interface ReadyNodeProfile {
  name: string;
  servers: ReadyNodeServer[];
}

export interface ReadyNodeFilterResponse {
  name: string; // Vendor name from API
  id: string; // Vendor ID from API
  profiles: ReadyNodeProfile[];
}

// API Response interface (matching actual backend response)
export interface ReadyNodeApiResponse {
  id: number; //- to be saved
  systemName?: string;
  vendor: string; //- to be saved
  vendorId?: string; //- to be saved
  storageType: string;
  vsanArchitecture?: string;
  infraType?: string;
  profile: string; //- to be saved
  generation: string; //- to be saved
  generationId?: string; // Generation ID (new field)
  model: string; //- to be saved
  supportedReleases: string[];
  vcgLink: string; //- to be saved
  cpuSockets: number;
  // The six flat defaultXxx fields below are dead on real responses - the backend nests them under
  // `defaults` (see that field further down) instead. Kept only as a fallback for a shape that's never
  // been observed on the wire (mapApiResponseToReadyNode's `apiData.defaults || {...}` already covers
  // it defensively) - same pattern that caused the totalMemory/memoryPerServer bug, just already guarded
  // here. Safe to delete once nobody's relying on the fallback.
  defaultCpuSockets?: number;
  maxCoresPerSocket?: number;
  defaultCoresPerSocket?: number;
  defaultMemory?: number;
  maxDriveCount: number;
  defaultDriveCount?: number;
  defaultCpuClockSpeed?: number;
  maxCpuClockSpeed?: number;
  minCpuClockSpeed?: number;
  esxiPreInstalled: boolean;
  servers: Array<{
    name: string;
    quantity: string;
    partner?: string;
    serverType: string;
  }>;
  cpus: Array<{
    name: string;
    cpuSockets: number;
    maxCores: number; // Updated from totalCores
    minCores: number; // New field
    minClockSpeed: number; // New field
    maxClockSpeed: number; // New field
    totalCores?: number; // Deprecated but kept for compatibility
    coresPerServer?: number | null; // Deprecated but kept for compatibility
  }>;
  memories: Array<{
    name?: string;
    // Always present on real responses - the backend's Memory model has no totalMemory field at all.
    memoryPerServer?: string | number;
    // Never populated on the wire today; kept only as a fallback for a future/alternate response shape.
    totalMemory?: string | number;
  }>;
  capacityDrives: Array<{
    name: string;
    partner: string;
    capacity: number;
    enduranceClass: string;
    performanceClass: string;
    formFactor: string;
    interfaceSpeed: string;
    deviceType: string;
    dwpd: number;
  }>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  cacheDrives?: any[];
  nics: Array<{
    name: string;
    partner: string;
    maxInterfaceSpeed: string | number; // Can be string or number
    ports: string;
  }>;
  defaults?: {
    cpuSockets: number;
    coresPerSocket: number;
    cpuClockSpeed: number;
    memory: number;
    driveCount: number;
    driveSize: number;
    nicSpeed: number;
  };
}

/**
 * Maps API response to internal ReadyNodeSelection model.
 * Ported as-is from ReadynodeDataService.mapApiResponseToReadyNode (readynode-data.service.ts:303-392).
 */
export function mapApiResponseToReadyNode(apiData: ReadyNodeApiResponse): VCPM.ReadyNodeSelection {
  const primaryCpu = apiData.cpus?.[0];
  const primaryMemory = apiData.memories?.[0];
  // Use the drive with the highest capacity so the datagrid shows the maximum supported values
  const primaryStorage = apiData.capacityDrives?.length
    ? apiData.capacityDrives.reduce((max, drive) => (drive.capacity > max.capacity ? drive : max))
    : undefined;

  const primaryNic = apiData.nics?.[0];
  // Handle maxInterfaceSpeed as either string or number
  const networkSpeed = primaryNic
    ? typeof primaryNic.maxInterfaceSpeed === 'number'
      ? primaryNic.maxInterfaceSpeed
      : parseInt(primaryNic.maxInterfaceSpeed)
    : 0;

  // Use defaults if available, otherwise use individual default fields
  const defaults = apiData.defaults || {
    cpuSockets: apiData.defaultCpuSockets || 0,
    coresPerSocket: apiData.defaultCoresPerSocket || 0,
    cpuClockSpeed: apiData.defaultCpuClockSpeed || 0,
    memory: apiData.defaultMemory || 0,
    driveCount: apiData.defaultDriveCount || 0,
    driveSize: 0,
    nicSpeed: 0,
  };

  // memoryPerServer is the field the backend actually populates; totalMemory is a fallback for a
  // response shape that has never been observed on the wire. Tested at the field level (not just
  // whether `primaryMemory` exists) so a memory entry present but missing both fields still falls
  // back to defaults.memory instead of producing NaN.
  const memoryValue = primaryMemory?.memoryPerServer ?? primaryMemory?.totalMemory;

  return {
    ui_row_id: apiData.systemName || `rn-${apiData.id}-${apiData.profile?.toLowerCase() || 'unknown'}`,
    readyNodeId: apiData.id?.toString() || '',
    detailsUrl: apiData.vcgLink || '',
    vendor: apiData.vendor || '',
    vendorId: apiData.vendorId || '',
    profileName: apiData.profile || '',
    serverModel: apiData.model || '',
    generation: apiData.generation || '',
    generationId: apiData.generationId || apiData.id?.toString() || '', // Use generationId if available
    cpuSeries: [apiData.generation || ''],
    storageDevice: primaryStorage?.name || 'N/A',
    capacity: primaryStorage?.capacity ? primaryStorage.capacity / 1000 : 0, // Convert GB to TB
    memory: memoryValue != null ? Number(memoryValue) : defaults.memory || 0,
    cpuSockets: apiData.cpuSockets || 0,
    cores: primaryCpu ? primaryCpu.maxCores || primaryCpu.totalCores || 0 : 0, // Use maxCores or fallback to totalCores
    maxDriveCount: apiData.maxDriveCount || 0,
    defaultDriveCount: defaults.driveCount || apiData.defaultDriveCount,
    defaultDriveSize: defaults.driveSize ? defaults.driveSize / 1000 : 0,
    defaultMemory: defaults.memory || apiData.defaultMemory,
    defaultCpuSockets: defaults.cpuSockets || apiData.defaultCpuSockets,
    defaultCoresPerSocket: defaults.coresPerSocket || apiData.defaultCoresPerSocket,
    maxCoresPerSocket: apiData.maxCoresPerSocket,
    defaultCpuClockSpeed: defaults.cpuClockSpeed || apiData.defaultCpuClockSpeed,
    minCpuClockSpeed: apiData.cpus?.length
      ? Math.min(...apiData.cpus.map((c) => c.minClockSpeed))
      : apiData.minCpuClockSpeed || 0,
    maxCpuClockSpeed: apiData.cpus?.length
      ? Math.max(...apiData.cpus.map((c) => c.maxClockSpeed))
      : apiData.maxCpuClockSpeed || 0,
    networkSpeed: defaults.nicSpeed || networkSpeed,
    minNetworkSpeed: 10, // Minimum is always 1 GbE
    maxNetworkSpeed: networkSpeed, // Maximum is the NIC's maxInterfaceSpeed
    validationStatus: 'PASS',
    selectableComponents: {
      storage: (apiData.capacityDrives || []).map((drive) => ({
        deviceModel: drive.name,
        capacity: drive.capacity / 1000, // Convert GB to TB
        units: 'TB',
        driveType: drive.deviceType,
        dwpd: drive.dwpd,
      })),
      memory: (apiData.memories || []).map((mem) => ({
        capacity: Number(mem.memoryPerServer ?? mem.totalMemory),
        units: 'GB',
      })),
      cpu: (apiData.cpus || []).map((cpu) => ({
        model: cpu.name || 'N/A',
        cores: cpu.maxCores || cpu.totalCores || 0,
        generation: apiData.generation,
        minClockSpeed: cpu.minClockSpeed,
        maxClockSpeed: cpu.maxClockSpeed,
        minCores: cpu.minCores,
        maxCores: cpu.maxCores,
      })),
      network: (apiData.nics || []).map((nic) => ({
        name: nic.name,
        partner: nic.partner || 'N/A',
        maxInterfaceSpeed: typeof nic.maxInterfaceSpeed === 'number' ? nic.maxInterfaceSpeed : parseInt(nic.maxInterfaceSpeed),
        ports: parseInt(nic.ports) || 1,
      })),
    },
  };
}

// Response shape of GET {dataProviderAPIUrl}readynodes/summary - one entry per catalog row, no
// pagination. Field-for-field mirror of the backend's ReadyNodeSummaryDto.
export interface ReadyNodeSummaryResponse {
  id: number;            // BCG catalog id - drives GET /v1/readynodes?systemName=... lookups indirectly via systemName
  systemName: string;    // stable across syncs - detail-fetch key, drives GET /v1/readynodes?systemName=...
  vendor: string;
  vendorId: string;
  profileName: string;
  server: string;        // filter-key, matches the search-filters tree's flat values
  model: string;         // display value (apiData.model equivalent)
  generation: string;
  generationId: string;
  capacityDriveName: string;
  // GB, distinct/sorted - same units as capacityDrives[].capacity. Replaces the old single `capacity`
  // field; the mapper below takes the max, so callers see the same whole-node-max semantics as before.
  capacities?: number[];
  memory: number;        // GB
  cpuSockets: number;
  maxCoresPerSocket: number;
  // Optional because the backend field is a nullable Integer (dataprovider PR #302) and older
  // deployments omit it entirely. With `capacity` it gives the node's whole-node raw capacity.
  maxDriveCount?: number;
  vcgLink: string;
  // GbE, max across the node's NICs - a single value is enough for pass/fail against a tier's
  // network-speed floor; nullable because not every catalog row has NIC data.
  maxNicSpeed?: number;
}

/**
 * Maps a GET .../readynodes/summary row to the same VCPM.ReadyNodeSelection shape the grid already
 * renders. Only the fields the catalog table displays (plus identity/filter-key fields), plus
 * maxDriveCount for the tier-threshold gate and maxNetworkSpeed for the Customize panel's seed
 * bounds, are populated - everything
 * customization-panel-only (selectableComponents, defaultXxx, min/maxClockSpeed, the default
 * networkSpeed) is intentionally left undefined here. It only exists on the full-detail shape
 * returned by the detail-by-id endpoint, applied once a row is actually selected.
 */
export function mapSummaryResponseToReadyNode(dto: ReadyNodeSummaryResponse): VCPM.ReadyNodeSelection {
  return {
    ui_row_id: dto.systemName || `rn-${dto.id}-${dto.profileName?.toLowerCase() || 'unknown'}`,
    readyNodeId: dto.id?.toString() || '',
    systemName: dto.systemName,
    detailsUrl: dto.vcgLink || '',
    vendor: dto.vendor || '',
    vendorId: dto.vendorId || '',
    profileName: dto.profileName || '',
    server: dto.server || '',
    serverModel: dto.model || '',
    generation: dto.generation || '',
    generationId: dto.generationId || '',
    cpuSeries: [dto.generation || ''],
    storageDevice: dto.capacityDriveName || 'N/A',
    capacity: dto.capacities?.length ? Math.max(...dto.capacities) / 1000 : 0,
    memory: dto.memory || 0,
    cpuSockets: dto.cpuSockets || 0,
    cores: dto.maxCoresPerSocket || 0,
    // Deliberately NOT `|| 0`: an absent value must stay nullish so the threshold gate's
    // meetsOrUnverifiable treats it as "can't verify" (passes), rather than a real 0 TB whole-node
    // capacity that would fail every tier's floor.
    maxDriveCount: dto.maxDriveCount,
    // Same nullish rule as maxDriveCount above - absent must stay absent, not 0.
    maxNetworkSpeed: dto.maxNicSpeed,
  };
}

/**
 * Extracts unique vendors from filter response.
 * Ported as-is from ReadynodeDataService.getVendorsFromFilters (readynode-data.service.ts:510-515).
 */
export function getVendorsFromFilters(filters: ReadyNodeFilterResponse[]): { id: string; name: string }[] {
  return filters
    .filter((f) => f.name !== undefined && f.name !== null)
    .map((f) => ({ id: f.id, name: f.name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Gets profiles for a specific vendor by ID.
 * Ported as-is from ReadynodeDataService.getProfilesForVendor (readynode-data.service.ts:520-524).
 */
export function getProfilesForVendor(filters: ReadyNodeFilterResponse[], vendorId: string): string[] {
  const vendorData = filters.find((f) => f.id === vendorId);
  if (!vendorData) return [];
  return vendorData.profiles.map((p) => p.name).sort();
}

/**
 * Gets server models for a specific vendor (by ID) and profile.
 * Ported as-is from ReadynodeDataService.getServerModelsForVendorAndProfile (readynode-data.service.ts:529-545).
 */
export function getServerModelsForVendorAndProfile(
  filters: ReadyNodeFilterResponse[],
  vendorId: string,
  profileName?: string,
): string[] {
  const vendorData = filters.find((f) => f.id === vendorId);
  if (!vendorData) return [];

  if (profileName) {
    const profile = vendorData.profiles.find((p) => p.name === profileName);
    return profile ? profile.servers.map((s) => s.name).sort() : [];
  }

  // Return all server models for the vendor across all profiles
  const allServers = vendorData.profiles.flatMap((p) => p.servers.map((s) => s.name));
  return [...new Set(allServers)].sort();
}

/**
 * Gets CPU generations for a specific server model.
 * Ported as-is from ReadynodeDataService.getGenerationsForServer (readynode-data.service.ts:550-574).
 */
export function getGenerationsForServer(
  filters: ReadyNodeFilterResponse[],
  vendorId: string,
  serverModel: string,
): { id: string; name: string }[] {
  const vendorData = filters.find((f) => f.id === vendorId);
  if (!vendorData) return [];

  const generationMap = new Map<string, { id: string; name: string }>();

  vendorData.profiles.forEach((profile) => {
    const server = profile.servers.find((s) => s.name === serverModel);
    if (server && server.generations) {
      // generations is an array of objects with {id, name}
      server.generations.forEach((gen) => {
        if (!generationMap.has(gen.id)) {
          generationMap.set(gen.id, { id: gen.id, name: gen.name });
        }
      });
    }
  });

  return Array.from(generationMap.values()).sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Filters ReadyNodes based on criteria (client-side filtering).
 * Ported as-is from ReadynodeDataService.filterReadyNodes (readynode-data.service.ts:579-604), then
 * extended with id-based vendor/generation matching and a fix to the serverModel criterion: it
 * matches node.server (the search-filters tree's flat value - what readynode-selector.component.ts's
 * own readyNodeFilters.serverModel field already holds today), NOT node.serverModel (the displayed
 * Model column, apiData.model). Those two differ on ~94% of real catalog rows (vendor-name prefix)
 * - matching against the wrong one would silently return zero rows for most of the catalog.
 */
export function filterReadyNodes(
  readyNodes: VCPM.ReadyNodeSelection[],
  criteria: ReadyNodeFilterCriteria,
): VCPM.ReadyNodeSelection[] {
  return readyNodes.filter((node) => {
    if (criteria.vendorId ? node.vendorId !== criteria.vendorId : criteria.vendor && node.vendor !== criteria.vendor) {
      return false;
    }
    if (criteria.profile && node.profileName !== criteria.profile) {
      return false;
    }
    if (criteria.generationId
      ? node.generationId !== criteria.generationId
      : criteria.generation && node.generation !== criteria.generation) {
      return false;
    }
    if (criteria.serverModel && node.server !== criteria.serverModel) {
      return false;
    }
    if (criteria.minCapacity && node.capacity < criteria.minCapacity) {
      return false;
    }
    if (criteria.maxCapacity && node.capacity > criteria.maxCapacity) {
      return false;
    }
    return true;
  });
}

// ---------------------------------------------------------------------------------------------------
// Cascading dropdown options derived directly from ReadyNodeSelection rows, rather than from the
// certification-tree filter metadata (getVendorsFromFilters/getProfilesForVendor/etc. above). For a
// catalog that's been narrowed by something the metadata tree knows nothing about (e.g. readOnly
// mode's rawThreshold GTE filter) - deriving options from the metadata tree there would list every
// vendor/profile/server/generation certified for the FULL catalog, most of which have zero rows
// actually meeting the threshold, silently mismatching the grid.
// ---------------------------------------------------------------------------------------------------

export function getVendorsFromReadyNodes(readyNodes: VCPM.ReadyNodeSelection[]): { id: string; name: string }[] {
  const vendors = new Map<string, string>();
  readyNodes.forEach((node) => {
    if (node.vendorId && !vendors.has(node.vendorId)) {
      vendors.set(node.vendorId, node.vendor);
    }
  });
  return Array.from(vendors, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
}

export function getProfilesFromReadyNodes(readyNodes: VCPM.ReadyNodeSelection[], vendorId: string): string[] {
  const profiles = new Set<string>();
  readyNodes.forEach((node) => {
    if (node.vendorId === vendorId && node.profileName) {
      profiles.add(node.profileName);
    }
  });
  return Array.from(profiles).sort();
}

/** `serverModel` matches node.server (the filter-key value), same convention as filterReadyNodes. */
export function getServerModelsFromReadyNodes(
  readyNodes: VCPM.ReadyNodeSelection[],
  vendorId: string,
  profile?: string,
): string[] {
  const servers = new Set<string>();
  readyNodes.forEach((node) => {
    if (node.vendorId !== vendorId) return;
    if (profile && node.profileName !== profile) return;
    if (node.server) servers.add(node.server);
  });
  return Array.from(servers).sort();
}

export function getGenerationsFromReadyNodes(
  readyNodes: VCPM.ReadyNodeSelection[],
  vendorId: string,
  serverModel: string,
): { id: string; name: string }[] {
  const generations = new Map<string, string>();
  readyNodes.forEach((node) => {
    if (node.vendorId === vendorId && node.server === serverModel && node.generationId) {
      generations.set(node.generationId, node.generation);
    }
  });
  return Array.from(generations, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
}

// ---------------------------------------------------------------------------------------------------
// HCI tier ladder + threshold filtering
//
// Shape of GET {dataProviderAPIUrl}readynode-profiles - the "ladder" describing each HCI tier's
// recommended per-node spec plus the standard sizes a node may be configured to.
// ---------------------------------------------------------------------------------------------------

export interface VSanProfileDto {
  profileCode: string; // matches VCPM.ReadyNodeSelection.profileName, e.g. "vSAN-HCI-MED"
  nodeCapacityTb: number; // whole-node raw capacity target, NOT a per-drive size
  cpuCoresPerNode: number; // total cores across all sockets, NOT per socket
  memoryGb: number;
  networkGbe: number;
  iopsPerNode: number;
  maxStorageDevices: number;
}

export interface WorkloadTierProfileDto {
  tier: string; // "SM" | "MED" | "LRG" - short form, NOT the same string as profile.profileCode
  profile: VSanProfileDto;
}

export interface GlobalConstantsDto {
  globalMaxCpuCores: number;
  globalMaxMemoryGb: number;
  esaClockSpeed: number;
  driveCapacitiesTb: number[]; // ladder of DRIVE sizes - belongs to workload-first sizing, unused here
  globalCpuSockets: number;
  cpuCoreSizes: number[];
  memorySizesGb: number[];
}

export interface ReadynodeConfigDto {
  tierProfiles: WorkloadTierProfileDto[];
  globalConstants: GlobalConstantsDto;
}

/**
 * Snaps `value` UP to the next rung of a ladder of standard sizes - never down to the nearest rung.
 * A 65-core requirement needs the 128 rung, not the 64 rung, because a 64-core node cannot satisfy it.
 * Returns `value` untouched when it exceeds every rung (clamping to the top rung would silently
 * understate a real requirement) or when the ladder is missing/empty.
 */
export function ceilToLadder(value: number, ladder: number[] | undefined): number {
  if (!ladder || ladder.length === 0) {
    return value;
  }
  // Copy before sorting - Array.sort mutates, and this ladder is shared config owned by the caller.
  const sorted = [...ladder].sort((a, b) => a - b);
  return sorted.find((rung) => rung >= value) ?? value;
}

interface TierRequirements {
  requiredCores: number;
  requiredMemoryGb: number;
  requiredCapacityTb: number;
}

/**
 * Resolves a tier code to its ceiling-snapped cores/memory requirement and its (un-snapped) capacity
 * requirement, for `filterReadyNodesByTierThreshold`'s generic-floor fallback path. Returns `null`
 * when the ladder isn't loaded yet or the tier code isn't recognised, so callers can fall back to
 * "leave the catalog alone".
 */
function resolveTierRequirements(
  tierProfiles: WorkloadTierProfileDto[],
  globalConstants: GlobalConstantsDto,
  targetProfileCode: string,
): TierRequirements | null {
  const target = tierProfiles?.find((tp) => tp.profile?.profileCode === targetProfileCode)?.profile;
  if (!target) {
    return null;
  }
  return {
    requiredCores: ceilToLadder(target.cpuCoresPerNode, globalConstants?.cpuCoreSizes),
    requiredMemoryGb: ceilToLadder(target.memoryGb, globalConstants?.memorySizesGb),
    requiredCapacityTb: target.nodeCapacityTb, // NOT ladder-snapped - driveCapacitiesTb is a DRIVE-size ladder (D3)
  };
}

/** Total cores per node, derived from the PER-SOCKET `cores` field - `undefined` if either factor is missing/zero. */
function totalCoresOf(node: VCPM.ReadyNodeSelection): number | undefined {
  return node.cpuSockets && node.cores ? node.cpuSockets * node.cores : undefined;
}

/** Whole-node raw capacity in TB (single-drive `capacity` x `maxDriveCount`) - `undefined` if either factor is missing/zero. */
function wholeNodeCapacityTbOf(node: VCPM.ReadyNodeSelection): number | undefined {
  return node.capacity && node.maxDriveCount ? node.capacity * node.maxDriveCount : undefined;
}

export interface RawThresholdRequirements {
  requiredCores?: number;
  requiredMemoryGb?: number;
  requiredCapacityTb?: number;
}

/**
 * Filters the catalog to nodes whose cores/memory/whole-node capacity all meet-or-exceed (GTE) the
 * given raw numeric requirements - the same GTE semantics `filterReadyNodesByTierThreshold` mirrors
 * from the dataprovider's real `/v1/readynodes/search` (`NumericSearchCriteria` gte, ANDed across
 * fields), but driven by explicit thresholds instead of resolving them from a tier/profile code. For
 * call sites whose requirement IS the domain's own actual computed cluster spec (cpu-based/
 * use-existing-cluster domains have no SM/MED/LRG tier code to rank against at all).
 *
 * A dimension with missing/zero node-side data, or an omitted/zero requirement, can never cause
 * exclusion (same graceful-degradation rule as `filterReadyNodesByTierThreshold`) - only a PROVEN
 * shortfall on a dimension with real data on both sides excludes a node.
 */
export function filterReadyNodesByRawThreshold(
  readyNodes: VCPM.ReadyNodeSelection[],
  requirements: RawThresholdRequirements,
): VCPM.ReadyNodeSelection[] {
  const meetsOrUnverifiable = (actual: number | undefined, required: number | undefined): boolean =>
    !actual || !required || actual >= required;

  return readyNodes.filter(
    (node) =>
      meetsOrUnverifiable(totalCoresOf(node), requirements.requiredCores) &&
      meetsOrUnverifiable(node.memory, requirements.requiredMemoryGb) &&
      meetsOrUnverifiable(wholeNodeCapacityTbOf(node), requirements.requiredCapacityTb),
  );
}

/**
 * Filters the catalog to nodes whose cores/memory/whole-node capacity all meet-or-exceed (GTE) the
 * selected HCI tier's requirement - mirroring the dataprovider's real `/v1/readynodes/search`
 * semantics (`NumericSearchCriteria` gte, ANDed across fields), replicated client-side against the
 * cached catalog so the tier-card click issues no new HTTP request.
 *
 * `profileName` is not consulted here at all - this function only ever checks the numeric fields.
 * Certification is the CALLER's responsibility: as of 2026-08-27, the tier-card call site ANDs this
 * with an exact-profileName match via `filterReadyNodes` before calling this (revising an earlier
 * 2026-08-14 decision to replace certification matching rather than AND it - a node certified for a
 * different tier that happened to meet this tier's numeric spec was included; that is no longer the
 * case). A caller that wants numeric-only matching can still call this standalone.
 *
 * A dimension with missing/zero node-side data can never cause exclusion - only a PROVEN shortfall
 * on a dimension with real data excludes a node. A node with no usable dimension at all is therefore
 * included, not excluded.
 *
 * CONFIRMED INTENTIONAL (2026-08-27), not a latent bug: `maxDriveCount` is null/undefined on any
 * dataprovider backend predating PR #302, which would fail every node on the capacity dimension if this
 * were fail-closed instead - on a lagging environment that's an empty grid, strictly worse than today's
 * false-positive risk (currently zero real occurrences in the live ESA catalog). Do not flip this to
 * fail-closed without first confirming every target environment is past that PR.
 */
export function filterReadyNodesByTierThreshold(
  readyNodes: VCPM.ReadyNodeSelection[],
  tierProfiles: WorkloadTierProfileDto[],
  globalConstants: GlobalConstantsDto,
  targetProfileCode: string,
): VCPM.ReadyNodeSelection[] {
  const requirements = resolveTierRequirements(tierProfiles, globalConstants, targetProfileCode);
  if (!requirements) {
    return readyNodes; // ladder not loaded, or an unrecognised tier - no threshold to gate on
  }
  return filterReadyNodesByRawThreshold(readyNodes, requirements);
}

// ---------------------------------------------------------------------------------------------------
// Fuzzy free-text search
// ---------------------------------------------------------------------------------------------------

const FREE_TEXT_SEARCH_KEYS = ['vendor', 'serverModel', 'systemName', 'generation', 'profileName', 'storageDevice', 'capacity'];

// Fuse builds its index eagerly in the constructor (O(catalog)), too expensive to redo on every
// debounced keystroke. The index is memoized against the array IDENTITY it was built from: the cached
// catalog is a stable array for the lifetime of a context (see ReadynodeDataService's shareReplay), so
// repeated searches reuse one index while any genuinely new array transparently gets a fresh one. Only
// the most recent index is retained - a 1-entry cache, not a growing map, so it cannot leak.
let cachedFuseSource: VCPM.ReadyNodeSelection[] | null = null;
let cachedFuse: Fuse<VCPM.ReadyNodeSelection> | null = null;

/**
 * Fuzzy free-text search over the catalog's identifying fields - vendor, displayed model, generation,
 * storage device, and the raw systemName/profileName (systemName often embeds vendor/model/profile as
 * one string, e.g. "PowerEdge R750:1:vSAN-HCI-SM", so it is included even though the parts already are).
 * Delegates BOTH matching and ranking to fuse.js: `fuse.search()` already returns results ordered
 * best-match-first, so this is a thin wrapper rather than a custom scorer.
 *
 * `ignoreLocation: true` stops Fuse penalising a match late in a longer string - the interesting part
 * of a catalog value is often at the end ("PowerEdge R750"), not the start.
 */
export function searchReadyNodesFreeText(readyNodes: VCPM.ReadyNodeSelection[], query: string): VCPM.ReadyNodeSelection[] {
  const normalized = query?.trim();
  if (!normalized) {
    return readyNodes; // no query - the caller's ordering (tier proximity, or catalog order) stands
  }
  if (cachedFuseSource !== readyNodes || !cachedFuse) {
    cachedFuse = new Fuse(readyNodes, {
      keys: FREE_TEXT_SEARCH_KEYS,
      threshold: 0.4,
      ignoreLocation: true,
    });
    cachedFuseSource = readyNodes;
  }
  return cachedFuse.search(normalized).map((result) => result.item);
}
